import { useEffect, useState } from "react";
import { Search, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormSection, Field } from "@/components/dossiers/FormSection";
import { fetchEntrepriseBySiren } from "@/lib/dossiers";
import { lookupEntreprise } from "@/lib/entreprises";

export default function EntrepriseSection({ entreprise, setEntreprise, autoSearch = 0, enImmat = false, setEnImmat }) {
    const [hint, setHint] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setEntreprise((s) => ({ ...s, [k]: e.target.value }));
    const sirenOk = /^\d{9}$/.test(entreprise.siren);

    async function search() {
        if (!sirenOk) return;
        setBusy(true);
        setHint(null);
        try {
            const local = await fetchEntrepriseBySiren(entreprise.siren);
            if (local) {
                setEntreprise((s) => ({ ...s, ...local }));
                setHint({ tone: "ok", text: "Entreprise déjà connue — informations pré-remplies depuis votre base." });
                return;
            }
            const p = await lookupEntreprise(entreprise.siren);
            setEntreprise((s) => ({
                ...s,
                nom_entreprise: p.nom_entreprise || s.nom_entreprise,
                forme_juridique: p.forme_juridique || s.forme_juridique,
                nom_gerant: p.nom_gerant || s.nom_gerant,
                adresse: p.adresse || s.adresse,
            }));
            setHint({ tone: "ok", text: `Fiche récupérée via Pappers${p.cached ? " (cache)" : ""} — vérifiez puis complétez.` });
        } catch (e) {
            setHint({ tone: "warn", text: `${e.message}. Vous pouvez saisir les informations manuellement.` });
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (!sirenOk) setHint(null);
    }, [sirenOk]);

    useEffect(() => {
        if (autoSearch > 0 && sirenOk) search();
    }, [autoSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <FormSection step={2} title="Entreprise" description="Saisissez le SIREN (9 chiffres) puis lancez la recherche : la fiche est pré-remplie via Pappers ou votre base." testId="entreprise-section">
            <Field id="siren" label="SIREN" required={!enImmat}>
                <div className="flex gap-2">
                    <Input
                        id="siren"
                        data-testid="siren-input"
                        inputMode="numeric"
                        maxLength={9}
                        disabled={enImmat}
                        placeholder="123456789"
                        className="h-11 bg-card font-mono tracking-wider max-w-xs disabled:opacity-50 disabled:bg-muted"
                        value={enImmat ? "" : entreprise.siren}
                        onChange={(e) => setEntreprise((s) => ({ ...s, siren: e.target.value.replace(/\D/g, "").slice(0, 9) }))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
                    />
                    <Button type="button" data-testid="siren-search-button" onClick={search} disabled={!sirenOk || busy || enImmat} variant="outline" className="h-11 rounded-full px-5 border-primary/20 text-primary hover:bg-accent">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" strokeWidth={2} />}
                        Rechercher
                    </Button>
                </div>
                <label className="flex items-center gap-2 pt-1.5 text-sm text-foreground cursor-pointer select-none">
                    <Checkbox data-testid="en-immatriculation-checkbox" checked={enImmat} onCheckedChange={(v) => setEnImmat?.(!!v)} />
                    Entreprise en cours d&apos;immatriculation (SIREN non encore attribué)
                </label>
                {hint && !enImmat && (
                    <p data-testid="siren-hint" className={`flex items-start gap-1.5 text-xs ${hint.tone === "ok" ? "text-emerald-700" : "text-amber-700"}`}>
                        {hint.tone === "ok" && <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                        {hint.text}
                    </p>
                )}
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field id="nom-entreprise" label="Raison sociale" required>
                    <Input id="nom-entreprise" data-testid="nom-entreprise-input" className="h-11 bg-card" value={entreprise.nom_entreprise} onChange={set("nom_entreprise")} />
                </Field>
                <Field id="forme-juridique" label="Forme juridique">
                    <Input id="forme-juridique" data-testid="forme-juridique-input" placeholder="SAS, SARL, SA…" className="h-11 bg-card" value={entreprise.forme_juridique ?? ""} onChange={set("forme_juridique")} />
                </Field>
                <Field id="nom-gerant" label="Dirigeant principal">
                    <Input id="nom-gerant" data-testid="nom-gerant-input" className="h-11 bg-card" value={entreprise.nom_gerant ?? ""} onChange={set("nom_gerant")} />
                </Field>
                <Field id="adresse" label="Adresse du siège">
                    <Input id="adresse" data-testid="adresse-input" className="h-11 bg-card" value={entreprise.adresse ?? ""} onChange={set("adresse")} />
                </Field>
            </div>
        </FormSection>
    );
}
